import i18n from "i18n/i18n";
import { get, isEmpty, isNil, toString, trim } from "lodash";

import { Alert } from "@texada/ui";

import {
  DEFAULT_US_ZIP_MASK,
  GW_CONFIG_KEYS,
  MAX_FILE_SIZE_LIMIT
} from "constants/AppConstants";
import { ISystemInfo, companyCurrencyCountryCode } from "./helper";

export const isBase64File = (file: string) =>
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{4})$/.test(
    file
  );

export const isValidEmail = (email: string) =>
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email.toLowerCase()
  );

export const isValidPhoneNumber = (value: string, length = 15) => {
  const digitsWithSymbols =
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(value);
  const onlyDigitsPass = /^\d{7,15}$/im.test(value);
  const anotherDigitsWithSymbols =
    /^(\+\d{1,3})?[-. ]?(\d{3}|\(\d{3}\))[-. ]?\d{3}[-. ]?\d{4}$/im.test(value);
  return (
    (onlyDigitsPass || digitsWithSymbols || anotherDigitsWithSymbols) &&
    value.length <= length &&
    value.length !== 0
  );
};

export const isMoreThan2DecimalPlaces = (number: number) =>
  toString(number).split(".")?.[1]?.trim()?.length > 2;

export const validateDate = (testdate: string) =>
  /^((17|18|19|2[0-9])[0-9]{2})-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(
    testdate
  );

export const isValidMoney = (value: string) =>
  /^[1-9]\d*(((,\d{3}){1})?((,\d{3}){1})?(\.\d{0,2})?)$/.test(value);

export const isValidMoneyLength = (value: string) =>
  toString(value).split(".")[0].length <= 13;

export const isValidURL = (url: string) =>
  /[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/.test(
    url
  );

export const isAlphaNumeric = (value: string) => /^[A-Za-z0-9]+$/.test(value);
export const hasBothAlphaNumeric = (value: string) =>
  /(?=.*\d)(?=.*[A-Za-z]).*/.test(value);
export const hasRepeatedCharacters = (value: string) => /(.)\1/.test(value);
export const hasOAuthPasswordCharacters = (value: string) =>
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*[\d])(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/.test(
    value
  );
export const isValidRate = (value: string) => /^[0-9\.]+$/.test(value);

export const hasSpecialCharacters = (value: string) =>
  /[^a-zA-Z0-9\_\\\-\/\s]/.test(value);

export const isValidPostalCode = (value: string) =>
  /^[a-zA-Z0-9- ]*$/.test(value);

export const validatePostalCode = (value: string, systemInfo?: any) => {
  const allCountryZipCodeValidation = () => {
    const noSymbol = /^[a-zA-Z0-9- ]*$/.test(value);
    if (!noSymbol) {
      return i18n.t("invalid_zip_code");
    }
    if (/^[0-9]{5}(?:-[0-9]{4})?$/.test(value)) {
      return null;
    }
    if (String(value).length > 10) {
      return i18n.t("zip_postal_code_can_not_be_more_than_characters", {
        count: 10
      });
    }
    return null;
  };

  const postalCodeFormat = trim(
    get(systemInfo, "company_postal_code_format", "")
  );
  if (isNil(systemInfo)) {
    return allCountryZipCodeValidation();
  } else if (postalCodeFormat === DEFAULT_US_ZIP_MASK) {
    if (!/^[0-9]{5}([a-zA-Z0-9-]{0,5})?$/.test(value)) {
      return i18n.t("zip_code_do_not_match_the_pattern", {
        pattern: "eg: 12345 or 12345-9878"
      });
    }
    if (value.length < 5 || value.length > 10) {
      return i18n.t("zip_postal_code_must_be_between_5_and_10_characters");
    }
    return null;
  } else if (postalCodeFormat) {
    const regexPattern = postalCodeFormat
      .replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&")
      .replaceAll("A", "[A-Z]")
      .replaceAll("9", "[0-9]");
    if (!new RegExp(regexPattern).test(value)) {
      return i18n.t("zip_code_do_not_match_the_pattern", {
        pattern: `eg: ${systemInfo?.company_postal_code_format}`
      });
    }
  } else {
    const company_currency = get(systemInfo, "company_currency", "");
    if (company_currency) {
      const mapCompanyCurrencyRegex = {
        [companyCurrencyCountryCode.CANADA]: {
          pattern: /^[A-Z]\d[A-Z] \d[A-Z]\d$/,
          error: i18n.t("zip_code_do_not_match_the_pattern", {
            pattern: "eg: M4B 1G5"
          })
        },
        [companyCurrencyCountryCode.UNITED_STATES]: {
          pattern: /^\d{5}$/,
          error: i18n.t("zip_code_do_not_match_the_pattern", {
            pattern: "eg: 99501"
          })
        }
      };
      const companyCurrencyRegex = mapCompanyCurrencyRegex[company_currency];
      if (companyCurrencyRegex && !companyCurrencyRegex.pattern.test(value)) {
        return companyCurrencyRegex.error;
      }
    }
  }
  return null;
};

export const validateBarcodes = (
  barcodes: string[],
  maxBarcodeLength: number = 20
) => {
  if (barcodes?.length < 1) return;

  let errors = {};
  barcodes.forEach((barcode, index) => {
    if (isEmpty(barcode.trim())) {
      errors[index] = "You can not add an empty barcode";
    } else if (barcode.length > maxBarcodeLength) {
      errors[index] = `Cannot be more than ${maxBarcodeLength} characters`;
    } else if (hasSpecialCharacters(barcode)) {
      errors[index] = "A barcode cannot contain special characters";
    }
  });

  if (isEmpty(errors)) {
    errors = null;
  }

  return errors;
};

export const validateProductNo = (value: string) =>
  /^[a-zA-Z0-9\-]+$/.test(value);

interface addressInterface {
  name?: string;
  city?: string;
  provState?: string;
  postalCode?: string;
  address1?: string;
  address2?: string;
  phone?: string;
}

export function validateAddressFields(
  {
    name,
    city,
    provState,
    postalCode,
    address1,
    address2,
    phone
  }: addressInterface,
  addressType?: "ticket",
  systemInfo?: ISystemInfo
) {
  let errors: addressInterface = {};

  if (provState.trim() === "") {
    errors.provState = i18n.t("state_province_is_required");
  } else if (provState.trim().length > 5) {
    errors.provState = i18n.t(
      "state_province_can_not_be_more_than_5_characters"
    );
  }

  if (name.trim() === "") {
    errors.name = i18n.t("name_is_required");
  } else if (name.trim().length > 30) {
    errors.name = i18n.t("this_field_can_not_be_more_than_characters", {
      count: 30
    });
  }

  if (city.trim() === "" && addressType === "ticket") {
    errors.city = i18n.t("city_is_required");
  } else if (city.trim().length > 20) {
    errors.city = i18n.t("this_field_can_not_be_more_than_characters", {
      count: 20
    });
  }

  if (postalCode.trim() === "") {
    errors.postalCode = i18n.t("zip_postal_code_is_required");
  }

  const zipcodeMessage = validatePostalCode(postalCode, systemInfo);
  if (postalCode && zipcodeMessage) {
    errors.postalCode = zipcodeMessage;
  }

  if (phone?.trim().length > 0 && validatePhoneNumberFormat(phone,systemInfo?.company_phone_number_format)) {
    errors.phone = validatePhoneNumberFormat(phone,systemInfo?.company_phone_number_format);
  }

  if (address1.trim() === "") {
    errors.address1 = i18n.t("address_is_required");
  } else if (address1.toString().length > 30) {
    errors.address1 = i18n.t("this_field_can_not_be_more_than_characters", {
      count: 30
    });
  }

  if (address2.toString().length > 30) {
    errors.address2 = i18n.t("this_field_can_not_be_more_than_characters", {
      count: 30
    });
  }

  return errors;
}

export const formatPhoneNumber = (value: string) => {
  var phoneInput = value
    .replace(/\D/g, "")
    .substring(0, 12)
    .match(/^(\d{0,3})(\d{0,3})(\d{0,6})$/);
  return !phoneInput[2]
    ? phoneInput[1]
    : `${phoneInput[1]}-${phoneInput[2]}` +
        (phoneInput[3] ? `-${phoneInput[3]}` : "");
};

export const snakeCaseString = (value: string) =>
  value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

export const validateGatewayConfigs = (
  configs: Models.IStoreConfigItem[],
  systemInfo?: ISystemInfo
) =>
  configs?.reduce((errors, config) => {
    const hasValue = config?.value?.trim() === "";
    switch (config.key) {
      case GW_CONFIG_KEYS.COMPANY_NAME:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("company_name_is_required")
          };
        }
        break;
      case GW_CONFIG_KEYS.MAIN_TITLE:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("main_title_is_required")
          };
        }
        break;
      case GW_CONFIG_KEYS.PHONE:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("phone_number_is_required")
          };
        } else if (!isValidPhoneNumber(config.value)) {
          return {
            ...errors,
            [config.key]: i18n.t("not_a_valid_phone_number")
          };
        }
        break;
      case GW_CONFIG_KEYS.POSTAL_CODE:
        const postalCodemessage = validatePostalCode(config?.value, systemInfo);
        if (config?.value?.trim() && postalCodemessage)
          return {
            ...errors,
            [config.key]: postalCodemessage
          };
        break;
      case GW_CONFIG_KEYS.PROV_STATE:
        if (config?.value?.trim()?.length > 5) {
          return {
            ...errors,
            [config.key]: i18n.t(
              "state_province_can_not_be_more_than_5_characters"
            )
          };
        }
        break;
      case GW_CONFIG_KEYS.CITY:
        if (config?.value?.trim()?.length > 20) {
          return {
            ...errors,
            [config.key]: i18n.t("this_field_can_not_be_more_than_characters", {
              count: 20
            })
          };
        }
        break;
      case GW_CONFIG_KEYS.PICKUP_REQUEST_EMAIL:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("pickup_request_email_is_required")
          };
        } else if (!isValidEmail(config.value)) {
          return {
            ...errors,
            [config.key]: i18n.t("invalid_email_address")
          };
        }
        break;
      case GW_CONFIG_KEYS.RESERVATION_REQUEST_EMAIL:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("reservation_request_email_is_required")
          };
        } else if (!isValidEmail(config.value)) {
          return {
            ...errors,
            [config.key]: i18n.t("invalid_email_address")
          };
        }
        break;
      case GW_CONFIG_KEYS.SERVICE_REQUEST_EMAIL:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("service_request_email_is_required")
          };
        } else if (!isValidEmail(config.value)) {
          return {
            ...errors,
            [config.key]: i18n.t("invalid_email_address")
          };
        }
        break;
      case GW_CONFIG_KEYS.DEFAULT_RENTAL_PERIOD_IN_DAYS:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("only_numbers_are_allowed")
          };
        }
        break;
      case GW_CONFIG_KEYS.MINIMUM_DAYS_OUT_FOR_RENTAL:
        if (hasValue) {
          return {
            ...errors,
            [config.key]: i18n.t("only_numbers_are_allowed")
          };
        }
        break;
      default:
        return errors;
    }
    return errors;
  }, {});

export const validatePhoneNumber = (format, number) => {
  const escapedFormat = format?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const excapedFormatUpper = escapedFormat?.toUpperCase();
  const regexPattern = excapedFormatUpper?.replace(/X/g, "\\d");
  const newFormat = new RegExp(`^${regexPattern}$`);
  return newFormat.test(number);
};

const isInvalidPhoneFormat = format =>
  isEmpty(format?.trim()) || format.toUpperCase().indexOf("X") === -1;

export function formatAndValidateNumber(input, format) {
  if (isInvalidPhoneFormat(format)) {
    return formatPhoneNumber(input);
  }

  const digitsOnly = input.replace(/\D/g, "");
  let formattedInput = "";
  let formatIndex = 0;
  let inputIndex = 0;

  while (formatIndex < format.length) {
    const formatChar = format.charAt(formatIndex);
    const inputChar =
      inputIndex < digitsOnly.length ? digitsOnly.charAt(inputIndex) : "";

    if (formatChar.toLowerCase() === "X".toLowerCase()) {
      formattedInput += inputChar;
      inputIndex++;
    } else if (inputChar === "") {
      formatIndex++;
    } else {
      formattedInput += formatChar;
    }
    formatIndex++;
  }
  return formattedInput;
}

const hasValidPhoneCharactersAndBalancedParentheses = phone => {
  const hasInvalidChars = /[^0-9()\-.+_/\s]/;

  if (hasInvalidChars.test(phone)) {
    return false;
  }

  let openParens = 0;
  for (const char of phone) {
    if (char === "(") openParens++;
    else if (char === ")") openParens--;
    if (openParens < 0) return false;
  }

  return openParens === 0;
};

export const validatePhoneNumberFormat = (phone, phoneNumberFormat) => {
  if (!phone) {
    return;
  }

  if (phoneNumberFormat && String(phoneNumberFormat).trim() === "") {
    return i18n.t("phone_number_format_cannot_be_blank_space");
  }

  const modifiedPhoneNumberFormat = isNil(phoneNumberFormat)
    ? ""
    : phoneNumberFormat;

  if (!hasValidPhoneCharactersAndBalancedParentheses(phone)) {
    return i18n.t("not_a_valid_phone_number");
  }

  if (isInvalidPhoneFormat(modifiedPhoneNumberFormat)) {
    if (isValidPhoneNumber(phone, 12)) {
      return i18n.t("not_a_valid_phone_number");
    }
  } else if (!validatePhoneNumber(modifiedPhoneNumberFormat, phone)) {
    return i18n.t("number_should_be_formatted", {
      value: modifiedPhoneNumberFormat
    });
  }
};

export const isValidLink = (link: string) => {
  const linkPattern =
    /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?(\?[^#\s]+)?(#[\/\w\-]*)?$/i;
  return linkPattern.test(link);
};

export const isValidForMultipleEmails = (emailTo: string) => {
  return emailTo.split(",").every(email => isValidEmail(email.trim()));
};

export const validateFileSize = (
  file: File,
  sizeLimit = MAX_FILE_SIZE_LIMIT
): boolean => {
  if (file.size > sizeLimit) {
    Alert.error({
      message: i18n.t("file_size_must_be_within_mb", {
        limit: sizeLimit / (1024 * 1024)
      })
    });
    return false;
  }
  return true;
};

export const hasValidFilesSize = (files, sizeLimit = MAX_FILE_SIZE_LIMIT) => {
  return files.every(file => {
    return validateFileSize(file, sizeLimit);
  });
};
